
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { Appointment, CancellationPolicy } from '@/types/dashboard';

interface CancellationModalProps {
  appointment: Appointment;
  userRole: 'psychologist' | 'patient';
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (appointmentId: string, reason: string) => Promise<void>;
  policy: CancellationPolicy;
}

/**
 * Modal para processar cancelamentos de consulta
 * Exibe política aplicável e solicita motivo do cancelamento
 */
const CancellationModal: React.FC<CancellationModalProps> = ({
  appointment,
  userRole,
  isOpen,
  onClose,
  onConfirm,
  policy
}) => {
  // Estados locais
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Calcula o tempo restante até a consulta em horas
  const hoursUntilAppointment = appointment.scheduledAt 
    ? Math.floor((new Date(appointment.scheduledAt).getTime() - new Date().getTime()) / (1000 * 60 * 60))
    : 0;

  // Determina se haverá reembolso com base na política e no tempo restante
  const willReceiveFullRefund = hoursUntilAppointment >= policy.noFeeThreshold;
  const willReceivePartialRefund = !willReceiveFullRefund && hoursUntilAppointment >= policy.partialRefundThreshold;
  const refundPercentage = willReceiveFullRefund ? 100 : (willReceivePartialRefund ? policy.partialRefundPercentage : 0);

  // Determina o texto de reembolso com base no papel do usuário
  const refundText = userRole === 'patient' 
    ? `Você ${refundPercentage > 0 ? `receberá um reembolso de ${refundPercentage}%` : 'não receberá reembolso'}`
    : `O paciente ${refundPercentage > 0 ? `receberá um reembolso de ${refundPercentage}%` : 'não receberá reembolso'}`;

  // Função para lidar com o cancelamento
  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('Por favor, informe o motivo do cancelamento');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(appointment.id, reason);
      setSuccess(true);
      // Após 2 segundos, fecha o modal
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setReason('');
      }, 2000);
    } catch (err) {
      setError('Ocorreu um erro ao cancelar a consulta. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para fechar o modal e resetar estados
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setReason('');
      setError(null);
      setSuccess(false);
    }
  };

  // Formatar data da consulta
  const formatDateTime = (dateTimeString?: string) => {
    if (!dateTimeString) return 'Data não disponível';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancelar Consulta</DialogTitle>
          <DialogDescription>
            Consulta agendada para {formatDateTime(appointment.scheduledAt)}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6">
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Consulta cancelada com sucesso!</AlertTitle>
              <AlertDescription>
                {userRole === 'psychologist' 
                  ? 'O paciente será notificado sobre o cancelamento.'
                  : 'O psicólogo será notificado sobre o cancelamento.'}
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <>
            <div className="py-4">
              <Alert variant={refundPercentage > 0 ? "default" : "destructive"}>
                {refundPercentage > 0 
                  ? <AlertCircle className="h-4 w-4" />
                  : <AlertTriangle className="h-4 w-4" />
                }
                <AlertTitle>Política de Cancelamento</AlertTitle>
                <AlertDescription>
                  <p>{refundText}</p>
                  <p className="text-sm mt-1">
                    • Cancelamento com {policy.noFeeThreshold}h ou mais de antecedência: reembolso de 100%<br />
                    • Cancelamento entre {policy.partialRefundThreshold}h e {policy.noFeeThreshold}h de antecedência: reembolso de {policy.partialRefundPercentage}%<br />
                    • Cancelamento com menos de {policy.partialRefundThreshold}h de antecedência: sem reembolso
                  </p>
                </AlertDescription>
              </Alert>
            </div>

            <div className="py-2">
              <label htmlFor="cancellation-reason" className="block text-sm font-medium mb-2">
                Motivo do cancelamento *
              </label>
              <Textarea
                id="cancellation-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Informe o motivo do cancelamento"
                rows={4}
                className="w-full"
                disabled={isSubmitting}
              />
              {error && (
                <p className="text-destructive text-sm mt-1">{error}</p>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Voltar
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleConfirm} 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processando...' : 'Confirmar Cancelamento'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancellationModal;
